import type { ReactElement } from 'react';
import type { GlobalSearchData } from './api';

export type GlobalFilter = 'all' | 'products' | 'posts' | 'pages';

const svg48 =
  'M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z';

function Icon48({ fill = '#ccc' }: { fill?: string }): ReactElement {
  return (
    <svg width="48px" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
      <path fill={fill} d={svg48} />
    </svg>
  );
}

export function GlobalSearchPlaceholderStart(): ReactElement {
  return (
    <div className="rnm-search-placeholder">
      <Icon48 fill="#ccc" />
      <p>Comienza a escribir para buscar...</p>
    </div>
  );
}

export function GlobalSearchPlaceholderShort(): ReactElement {
  return (
    <div className="rnm-search-placeholder">
      <Icon48 fill="#ccc" />
      <p>Escribe al menos 2 caracteres</p>
    </div>
  );
}

export function GlobalSearchPlaceholderDesktop(): ReactElement {
  return (
    <div className="rnm-search-placeholder">
      <Icon48 fill="#ccc" />
      <p>Escribe para buscar productos...</p>
    </div>
  );
}

export function GlobalSearchLoading(): ReactElement {
  return (
    <div className="rnm-loading">
      <div className="rnm-spinner" />
      <p>Buscando...</p>
    </div>
  );
}

interface GlobalSearchResultsProps {
  data: GlobalSearchData;
  activeFilter: GlobalFilter;
  onFilterChange: (f: GlobalFilter) => void;
}

export function GlobalSearchResultsView({
  data,
  activeFilter,
  onFilterChange,
}: GlobalSearchResultsProps): ReactElement {
  const hasProducts = data.results.products && data.results.products.length > 0;
  const hasPosts = data.results.posts && data.results.posts.length > 0;
  const hasPages = data.results.pages && data.results.pages.length > 0;

  let html = '';

  if (hasProducts || hasPosts || hasPages) {
    html += '<div class="rnm-search-filters">';
    html +=
      '<button type="button" class="rnm-filter-pill' +
      (activeFilter === 'all' ? ' active' : '') +
      '" data-filter="all">Todos</button>';
    if (hasProducts) {
      html +=
        '<button type="button" class="rnm-filter-pill' +
        (activeFilter === 'products' ? ' active' : '') +
        '" data-filter="products">Productos (' +
        data.results.products.length +
        ')</button>';
    }
    if (hasPosts) {
      html +=
        '<button type="button" class="rnm-filter-pill' +
        (activeFilter === 'posts' ? ' active' : '') +
        '" data-filter="posts">Blog (' +
        data.results.posts.length +
        ')</button>';
    }
    if (hasPages) {
      html +=
        '<button type="button" class="rnm-filter-pill' +
        (activeFilter === 'pages' ? ' active' : '') +
        '" data-filter="pages">Páginas (' +
        data.results.pages.length +
        ')</button>';
    }
    html += '</div>';
  }

  if (hasProducts && (activeFilter === 'all' || activeFilter === 'products')) {
    html += '<div class="rnm-search-section" data-section="products"><h3 class="rnm-search-section-title">Productos</h3><div class="rnm-search-products">';
    data.results.products.forEach((product) => {
      html += '<a href="' + escapeAttr(product.url) + '" class="rnm-search-product">';
      html +=
        '<div class="rnm-product-image"><img src="' +
        escapeAttr(product.image) +
        '" alt="' +
        escapeAttr(product.title) +
        '" loading="lazy" /></div>';
      html += '<div class="rnm-product-info">';
      html += '<h4 class="rnm-product-title">' + escapeHtml(product.title) + '</h4>';
      if (product.sku) {
        html += '<p class="rnm-product-sku">SKU: ' + escapeHtml(product.sku) + '</p>';
      }
      html += '<div class="rnm-product-price">' + product.price + '</div>';
      if (!product.in_stock) {
        html += '<span class="rnm-product-stock">Agotado</span>';
      }
      html += '</div></a>';
    });
    html += '</div></div>';
  }

  if (hasPosts && (activeFilter === 'all' || activeFilter === 'posts')) {
    html += '<div class="rnm-search-section" data-section="posts"><h3 class="rnm-search-section-title">Blog</h3><div class="rnm-search-posts">';
    data.results.posts.forEach((post) => {
      html += '<a href="' + escapeAttr(post.url) + '" class="rnm-search-post">';
      html += '<h4 class="rnm-post-title">' + escapeHtml(post.title) + '</h4>';
      html += '<p class="rnm-post-excerpt">' + escapeHtml(post.excerpt) + '</p>';
      html += '<span class="rnm-post-date">' + escapeHtml(post.date) + '</span>';
      html += '</a>';
    });
    html += '</div></div>';
  }

  if (hasPages && (activeFilter === 'all' || activeFilter === 'pages')) {
    html += '<div class="rnm-search-section" data-section="pages"><h3 class="rnm-search-section-title">Páginas</h3><div class="rnm-search-pages">';
    data.results.pages.forEach((page) => {
      html += '<a href="' + escapeAttr(page.url) + '" class="rnm-search-page">';
      html += '<h4 class="rnm-page-title">' + escapeHtml(page.title) + '</h4>';
      if (page.excerpt) {
        html += '<p class="rnm-page-excerpt">' + escapeHtml(page.excerpt) + '</p>';
      }
      html += '</a>';
    });
    html += '</div></div>';
  }

  return (
    <div
      className="rnm-global-results-host"
      dangerouslySetInnerHTML={{ __html: html }}
      onClick={(e) => {
        const t = (e.target as HTMLElement).closest(
          '.rnm-filter-pill'
        ) as HTMLElement | null;
        if (!t) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        const f = t.getAttribute('data-filter') as GlobalFilter | null;
        if (f === 'all' || f === 'products' || f === 'posts' || f === 'pages') {
          onFilterChange(f);
        }
      }}
    />
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, '&#39;');
}
